import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { logMerchantTypeChange } from '@/utils/activityLogger';
import { saveMerchantStatus } from '@/utils/organization/saveMerchantStatus';
import { getMerchantTypeLabel, MerchantType } from '@/components/admin/MerchantTypeSelector';
import { MerchantStatusBadge } from '@/components/admin/merchants/MerchantStatusBadge';
import {
  MerchantHistoryEntry,
  lastChangeOf,
  merchantHistoryQueryKey,
  useMerchantHistory,
} from '@/hooks/useMerchantHistory';
import {
  deriveMerchantStatus,
  getMerchantStatus,
  MERCHANT_STATUS_GROUPS,
  MERCHANT_STATUSES,
  type MerchantStatusId,
} from '@/shared/merchantStatus';
import { MerchantRecord } from '@/shared/types/merchant';
import { cn } from '@/lib/utils';
import { EditButton } from './EditButton';
import { SettingsRow, SettingsSection } from './SettingsGroup';

// Merchant Classification (FIG-320 / REQ-002–004, 010, 012, 024, 025; FIG-345
// item 4 · UC-227). Type and Status are edited in place, one at a time, and each
// change is recorded. Status goes through set-merchant-status-v1 (admin-only on
// the server, with a stale-write check); choosing Archived asks for a simple
// confirmation first. The outstanding-amount warning and Outstanding marker
// wait for Deployed Consideration (future iteration).

interface MerchantClassificationSectionProps {
  merchant: MerchantRecord;
  canEdit: boolean;
  onUpdated: () => void;
}

const TYPES: MerchantType[] = ['direct', 'reseller'];

type Field = 'type' | 'status';
type SavedState = false | 'saved' | 'not-recorded';

const formatWhen = (iso: string) => format(new Date(iso), 'MMM d, yyyy');

const changedLine = (entry: MerchantHistoryEntry | undefined) =>
  entry ? `Changed by ${entry.by} · ${formatWhen(entry.at)}` : null;

const SavedNote: React.FC<{ state: SavedState }> = ({ state }) =>
  state === 'saved' ? (
    <span role="status" className="text-sm text-emerald-700">Saved · recorded in User Activity</span>
  ) : state === 'not-recorded' ? (
    <span role="status" className="text-sm text-amber-800">Saved, but not recorded in User Activity</span>
  ) : null;

export const MerchantClassificationSection: React.FC<MerchantClassificationSectionProps> = ({
  merchant,
  canEdit,
  onUpdated,
}) => {
  const queryClient = useQueryClient();
  const { data: history, isError: historyError } = useMerchantHistory(merchant.id);
  const currentType = (merchant.merchant_type || 'direct') as MerchantType;
  const currentStatus = deriveMerchantStatus(merchant);
  const status = getMerchantStatus(currentStatus);

  // Only one field (Type or Status) is open at a time (REQ-024).
  const [openField, setOpenField] = useState<Field | null>(null);
  const [draftType, setDraftType] = useState<MerchantType>(currentType);
  const [draftStatus, setDraftStatus] = useState<MerchantStatusId>(currentStatus);
  // The Status as it was when the editor opened. Sent as expectedCurrent, so a
  // refresh during the edit (e.g. another admin archived it) still gets a 409
  // instead of being overwritten (Greptile P1 on #288).
  const [openedStatus, setOpenedStatus] = useState<MerchantStatusId>(currentStatus);
  const [saving, setSaving] = useState(false);
  const [typeError, setTypeError] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ field: Field; state: SavedState } | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(null), 4000);
    return () => clearTimeout(timer);
  }, [saved]);

  // Losing edit rights (admin mode off) closes any open field.
  useEffect(() => {
    if (!canEdit) {
      setOpenField(null);
      setConfirmArchive(false);
    }
  }, [canEdit]);

  const open = (field: Field) => {
    setDraftType(currentType);
    setDraftStatus(currentStatus);
    setOpenedStatus(currentStatus);
    setTypeError(false);
    setStatusError(null);
    setSaved(null);
    setOpenField(field);
  };

  const editingType = canEdit && openField === 'type';
  const editingStatus = canEdit && openField === 'status';

  const refreshHistory = () => queryClient.invalidateQueries({ queryKey: merchantHistoryQueryKey(merchant.id) });

  const saveType = async () => {
    if (!canEdit) return;
    setSaving(true);
    setTypeError(false);
    try {
      const { error } = await supabase
        .from('organisations')
        .update({ merchant_type: draftType })
        .eq('id', merchant.id);
      if (error) throw error;

      const { data: { user } } = await supabase.auth.getUser();
      let recorded = false;
      if (user?.email) {
        recorded = await logMerchantTypeChange(user.email, merchant.id, merchant.name, currentType, draftType);
      }
      await refreshHistory();
      setOpenField(null);
      setSaved({ field: 'type', state: recorded ? 'saved' : 'not-recorded' });
      onUpdated();
    } catch (error) {
      console.error('Failed to save merchant type', error);
      setTypeError(true);
    } finally {
      setSaving(false);
    }
  };

  const saveStatus = async () => {
    if (!canEdit || draftStatus === openedStatus) return;
    setConfirmArchive(false);
    setSaving(true);
    setStatusError(null);
    const result = await saveMerchantStatus(merchant.id, draftStatus, openedStatus);
    setSaving(false);
    if (!result.ok) {
      setStatusError(result.conflict
        ? `Someone else changed it to ${isKnownStatus(result.conflict.current) ? getMerchantStatus(result.conflict.current).label : result.conflict.current} meanwhile. The latest value is shown; choose again if needed.`
        : `${result.error}. The previous status was kept.`);
      if (result.conflict) {
        // Start again from the latest value, so the admin's next choice is
        // checked against it rather than the stale one (Greptile P2 on #288).
        const latest = result.conflict.current;
        if (isKnownStatus(latest)) {
          setOpenedStatus(latest);
          setDraftStatus(latest);
        }
        onUpdated();
      }
      return;
    }
    await refreshHistory();
    setOpenField(null);
    setSaved({ field: 'status', state: result.recorded ? 'saved' : 'not-recorded' });
    onUpdated();
  };

  // Archived always asks for a simple confirmation first (REQ-013, AC-009).
  const requestSaveStatus = () => {
    if (draftStatus === 'archived') setConfirmArchive(true);
    else void saveStatus();
  };

  const entries = history ?? [];

  return (
    <SettingsSection title="Merchant Classification">
      <SettingsRow
        label="Type"
        editing={editingType}
        testId="classification-type"
        action={canEdit && !openField ? <EditButton label="Edit type" onClick={() => open('type')} /> : null}
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">{getMerchantTypeLabel(currentType)}</span>
          {saved?.field === 'type' && <SavedNote state={saved.state} />}
        </div>
        {changedLine(lastChangeOf(entries, 'merchant_type_change')) && (
          <div className="mt-1 text-xs text-muted-foreground">{changedLine(lastChangeOf(entries, 'merchant_type_change'))}</div>
        )}
        {editingType && (
          <div className="mt-3 space-y-3">
            <fieldset>
              <legend className="mb-2 text-sm font-medium">Choose a type</legend>
              <div className="flex flex-wrap gap-2">
                {TYPES.map((type) => (
                  <label
                    key={type}
                    className={cn(
                      'flex min-w-[160px] cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm',
                      draftType === type && 'border-primary bg-primary/5',
                    )}
                  >
                    <input
                      type="radio"
                      name={`merchant-type-${merchant.id}`}
                      checked={draftType === type}
                      onChange={() => setDraftType(type)}
                      disabled={saving}
                    />
                    <span>{getMerchantTypeLabel(type)}</span>
                    {type === currentType && <span className="ml-auto text-xs text-muted-foreground">Current</span>}
                  </label>
                ))}
              </div>
            </fieldset>
            {typeError && (
              <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <strong>Type not saved.</strong> Please try again or contact support. The previous type was kept.
              </div>
            )}
            <div className="flex gap-2">
              <Button type="button" onClick={saveType} disabled={saving || draftType === currentType}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save type
              </Button>
              <Button type="button" variant="outline" onClick={() => setOpenField(null)} disabled={saving}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </SettingsRow>

      <SettingsRow
        label="Status"
        editing={editingStatus}
        testId="classification-status"
        action={canEdit && !openField ? <EditButton label="Edit status" onClick={() => open('status')} /> : null}
      >
        <div className="flex items-center gap-2">
          <MerchantStatusBadge status={status.id} />
          {saved?.field === 'status' && <SavedNote state={saved.state} />}
        </div>
        <div className="mt-1 text-sm text-muted-foreground">{status.meaning}</div>
        {changedLine(lastChangeOf(entries, 'merchant_status_change')) && (
          <div className="mt-1 text-xs text-muted-foreground">{changedLine(lastChangeOf(entries, 'merchant_status_change'))}</div>
        )}
        {editingStatus && (
          <div className="mt-3 space-y-3">
            {MERCHANT_STATUS_GROUPS.map((group) => (
              <fieldset key={group}>
                <legend className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group}</legend>
                <div className="space-y-1.5">
                  {MERCHANT_STATUSES.filter((s) => s.group === group).map((option) => (
                    <label
                      key={option.id}
                      className={cn(
                        'flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 text-sm',
                        draftStatus === option.id && 'border-primary bg-primary/5',
                      )}
                    >
                      <input
                        type="radio"
                        className="mt-1"
                        name={`merchant-status-${merchant.id}`}
                        checked={draftStatus === option.id}
                        onChange={() => setDraftStatus(option.id)}
                        // Choices are locked while a save is running (Greptile P2 on #288).
                        disabled={saving}
                        aria-describedby={`status-meaning-${merchant.id}-${option.id}`}
                      />
                      <span className="min-w-0">
                        <span className="font-medium">{option.label}</span>
                        <span id={`status-meaning-${merchant.id}-${option.id}`} className="block text-xs text-muted-foreground">
                          {option.meaning}
                        </span>
                      </span>
                      {option.id === openedStatus && <span className="ml-auto text-xs text-muted-foreground">Current</span>}
                    </label>
                  ))}
                </div>
              </fieldset>
            ))}
            {statusError && (
              <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <strong>Status not saved.</strong> {statusError}
              </div>
            )}
            <div className="flex gap-2">
              <Button type="button" onClick={requestSaveStatus} disabled={saving || draftStatus === openedStatus}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save status
              </Button>
              <Button type="button" variant="outline" onClick={() => setOpenField(null)} disabled={saving}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </SettingsRow>

      <AlertDialog open={confirmArchive} onOpenChange={(next) => { if (!next) setConfirmArchive(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {merchant.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The merchant leaves the default Merchants list. It shows again when Archived is ticked in the Status filter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void saveStatus()}>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="border-t pt-3">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0"
          aria-expanded={historyOpen}
          onClick={() => setHistoryOpen((isOpen) => !isOpen)}
        >
          {historyOpen ? 'Hide history' : `View history (${entries.length})`}
        </Button>
        {historyOpen && (
          <div className="mt-3 space-y-2">
            {historyError && <p role="alert" className="text-sm text-destructive">History could not be loaded.</p>}
            {entries.length === 0 && !historyError ? (
              <p className="text-sm text-muted-foreground">No changes recorded yet.</p>
            ) : (
              <ol className="space-y-2">
                {entries.map((entry) => (
                  <li key={entry.id} className="flex flex-wrap justify-between gap-2 text-sm">
                    <span>{entry.text}</span>
                    <span className="text-muted-foreground">{entry.by} · {formatWhen(entry.at)}</span>
                  </li>
                ))}
              </ol>
            )}
            <p className="text-xs text-muted-foreground">Also recorded in Admin Dashboard › User Activity.</p>
          </div>
        )}
      </div>
    </SettingsSection>
  );
};

const isKnownStatus = (value: string): value is MerchantStatusId =>
  MERCHANT_STATUSES.some((s) => s.id === value);

export default MerchantClassificationSection;
